/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { Star } from 'lucide-react'
import React, { useState, useRef, useEffect } from 'react'

import { Button } from '@/components/ui/Button'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type TestimonialProps = {
  /** The source logo of the company that the testimonial is for. */
  logo: string

  /** The rating of the testimonial. */
  rating: number

  /** The review of the testimonial. */
  review: React.ReactNode

  /** The author of the testimonial. */
  author: React.ReactNode

  /** Optional class name(s) to apply to the testimonial. */
  className?: string

  /** Maximum height in pixels before showing the "show more" button. Defaults to 100px. */
  maxHeight?: number
}

/**
 * A testimonial component that displays a company logo, rating, review, author and date. Designed to be used inside
 * a grid, as it utilises a subgrid layout to position the elements correctly. The review content can be expanded
 * if it exceeds the maximum height using a "show more" button.
 */
const Testimonial: React.FC<TestimonialProps> = ({ logo, rating, review, author, className, maxHeight = 100 }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showButton, setShowButton] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<string>('auto')

  useEffect(() => {
    const checkHeight = () => {
      if (contentRef.current) {
        const shouldShowButton = contentRef.current.scrollHeight > maxHeight
        setShowButton(shouldShowButton)
        setHeight(shouldShowButton ? `${maxHeight}px` : 'auto')
      }
    }

    checkHeight()

    const resizeObserver = new ResizeObserver(checkHeight)
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [maxHeight])

  return (
    <div
      className={cn(
        'relative grid grid-rows-subgrid row-span-4 bg-white rounded-lg shadow-mta mt-6 pt-12 pb-6 px-6 text-center',
        className,
      )}
    >
      <div className={'absolute left-1/2 -translate-x-1/2 -top-8'}>
        <div className={'w-16 h-16 rounded-full bg-white shadow-mta p-2 flex items-center justify-center'}>
          <img src={logo} alt={'Company logo'} className={'w-10 h-10 object-contain'} />
        </div>
      </div>

      <div className={'flex flex-row justify-center'}>
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={cn('w-5 h-5 fill-gray-200 text-gray-200', i < rating && 'fill-yellow-400 text-yellow-400')}
          />
        ))}
      </div>

      <div className={'relative'}>
        <div
          className={'overflow-hidden'}
          style={{
            height: isExpanded ? contentRef.current?.scrollHeight + 'px' : height,
            transition: 'height 300ms ease-in-out',
          }}
        >
          <div
            ref={contentRef}
            className={cn('text-gray-700 text-pretty mb-8', showButton && 'cursor-pointer')}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {review}
          </div>
        </div>

        {!isExpanded && showButton && (
          <div
            className={
              'absolute bottom-6 left-0 right-0 h-16 bg-linear-to-t from-white to-transparent pointer-events-none transition-opacity duration-300'
            }
          />
        )}

        {showButton && (
          <div className={'flex bottom-0 w-full left-0 right-0'}>
            <Button
              className={'w-full text-center text-gray-600'}
              variant={'ghost'}
              size={'sm'}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </Button>
          </div>
        )}
      </div>

      <div className={'text-sm font-normal text-gray-500'}>{author}</div>
    </div>
  )
}

export { Testimonial }
export type { TestimonialProps }
